/**
 * Raiz de la aplicacion: sesion, organizacion activa, enrutado y tiempo real.
 *
 * La sesion vive aqui; el resto de pantallas reciben lo que necesitan por
 * props. El token se guarda en `localStorage` y se restaura con `/api/auth/me`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { api, errorMessage, setApiAuth, setUnauthorizedHandler } from './api.ts';
import { AiSettingsScreen } from './components/AiSettingsScreen.tsx';
import { AccountsScreen } from './components/AccountsScreen.tsx';
import { ApiKeysScreen } from './components/ApiKeysScreen.tsx';
import { AppShell, PlatformStaffWarning, TenantWarning } from './components/AppShell.tsx';
import { ContactsScreen } from './components/ContactsScreen.tsx';
import { Spinner } from './components/Feedback.tsx';
import { InboxScreen } from './components/InboxScreen.tsx';
import { KnowledgeScreen } from './components/KnowledgeScreen.tsx';
import { LoginScreen } from './components/LoginScreen.tsx';
import { MetricsScreen } from './components/MetricsScreen.tsx';
import { TeamScreen } from './components/TeamScreen.tsx';
import { TemplatesScreen } from './components/TemplatesScreen.tsx';
import { TenantsScreen } from './components/TenantsScreen.tsx';
import { navigate, useHashRoute, useNotices } from './hooks.ts';
import { realtimeHub } from './sse.ts';
import type { Session } from './state.ts';
import {
  DEFAULT_ROUTE,
  activeRole,
  canManageSettings,
  clearStoredSession,
  pickTenantId,
  readStoredSession,
  storeSession,
} from './state.ts';

export function App(): ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const route = useHashRoute();
  const { notices, push, dismiss } = useNotices();

  const logout = useCallback((reason?: string): void => {
    clearStoredSession();
    setApiAuth(null);
    realtimeHub.disconnect();
    setSession(null);
    setLoginError(reason ?? null);
  }, []);

  // Un 401 en cualquier peticion cierra la sesion y vuelve al login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout('La sesion ha caducado. Vuelve a iniciar sesion.');
    });
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  // Restauracion de la sesion al recargar la pagina.
  useEffect(() => {
    const stored = readStoredSession();
    if (!stored) {
      setRestoring(false);
      return;
    }

    setApiAuth(stored);
    let cancelled = false;

    api
      .me()
      .then((response) => {
        if (cancelled) return;
        const tenantId = pickTenantId(response.memberships, stored.tenantId);
        setApiAuth({ token: stored.token, tenantId });
        storeSession(stored.token, tenantId);
        setSession({
          token: stored.token,
          user: response.user,
          memberships: response.memberships,
          tenantId,
        });
      })
      .catch(() => {
        if (cancelled) return;
        clearStoredSession();
        setApiAuth(null);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Conexion SSE: se reabre si cambia el token o la organizacion activa.
  const token = session?.token ?? null;
  const tenantId = session?.tenantId ?? null;
  useEffect(() => {
    if (!token) {
      realtimeHub.disconnect();
      return;
    }
    realtimeHub.connect(token, tenantId ?? undefined);
    return () => realtimeHub.disconnect();
  }, [token, tenantId]);

  const handleLogin = useCallback(
    (email: string, password: string): void => {
      setSubmitting(true);
      setLoginError(null);
      api
        .login({ email, password })
        .then((response) => {
          const nextTenantId = pickTenantId(response.memberships, null);
          setApiAuth({ token: response.token, tenantId: nextTenantId });
          storeSession(response.token, nextTenantId);
          setSession({
            token: response.token,
            user: response.user,
            memberships: response.memberships,
            tenantId: nextTenantId,
          });
          navigate(DEFAULT_ROUTE);
          if (response.memberships.length === 0) {
            push('warning', 'Tu usuario no pertenece a ninguna organizacion.');
          }
        })
        .catch((error: unknown) => setLoginError(errorMessage(error)))
        .finally(() => setSubmitting(false));
    },
    [push],
  );

  const handleSwitchTenant = useCallback(
    (nextTenantId: string): void => {
      if (!session) return;
      setApiAuth({ token: session.token, tenantId: nextTenantId });
      storeSession(session.token, nextTenantId);
      setSession({ ...session, tenantId: nextTenantId });
    },
    [session],
  );

  const handleOpenConversation = useCallback((conversationId: string | null): void => {
    navigate({ name: 'inbox', conversationId });
  }, []);

  if (restoring) {
    return (
      <div className="splash">
        <Spinner label="Restaurando la sesion…" />
      </div>
    );
  }

  if (!session) {
    return <LoginScreen onSubmit={handleLogin} submitting={submitting} error={loginError} />;
  }

  const renderRoute = (): ReactElement => {
    // Gestion de organizaciones: exclusiva del personal de la plataforma y
    // fuera de cualquier organizacion, por eso se resuelve antes del aviso de
    // "sin organizacion activa".
    if (route.name === 'tenants') {
      if (!session.user.isSuperAdmin) return <PlatformStaffWarning />;
      return <TenantsScreen onNotice={push} />;
    }

    if (session.memberships.length === 0) return <TenantWarning />;

    // Escribir la configuracion exige rol de administrador en la API.
    const canWrite = canManageSettings(activeRole(session));

    switch (route.name) {
      case 'inbox':
        return (
          <InboxScreen
            session={session}
            conversationId={route.conversationId}
            onOpenConversation={handleOpenConversation}
            onNotice={push}
          />
        );
      case 'metrics':
        return <MetricsScreen />;
      case 'ai':
        return <AiSettingsScreen onNotice={push} canWrite={canWrite} />;
      case 'knowledge':
        return <KnowledgeScreen onNotice={push} canWrite={canWrite} />;
      case 'team':
        return <TeamScreen />;
      case 'contacts':
        return (
          <ContactsScreen
            onNotice={push}
            canWrite={canWrite}
            onOpenConversation={handleOpenConversation}
          />
        );
      case 'accounts':
        return <AccountsScreen onNotice={push} canWrite={canWrite} />;
      case 'templates':
        return <TemplatesScreen onNotice={push} canWrite={canWrite} />;
      case 'apiKeys':
        return <ApiKeysScreen onNotice={push} canWrite={canWrite} />;
    }
  };

  return (
    <AppShell
      session={session}
      route={route}
      onNavigate={navigate}
      onLogout={() => logout()}
      onSwitchTenant={handleSwitchTenant}
      notices={notices}
      onDismissNotice={dismiss}
    >
      {renderRoute()}
    </AppShell>
  );
}
